import type { Audience, AudienceEstimate, Channel, ComposeRequest, InlineContent, TopicClass } from 'types/engagement.types';

/**
 * Rules of the compose screen, kept pure so they are testable without a DOM.
 * The API enforces the same rules (409 without testId above 20, approval at "all" or > 500) —
 * the UI only makes them visible before the click.
 */

export const TEST_REQUIRED_ABOVE = 20;
export const APPROVAL_REQUIRED_ABOVE = 500;

export interface ComposeState {
  mode: 'template' | 'free';
  templateKey: string;
  content: InlineContent;
  topic: string;
  topicClass: TopicClass;
  channels: Channel[];
  audience: Audience;
  delivery: 'window' | 'immediate';
  variableValues: Record<string, string>;
}

export const initialComposeState = (): ComposeState => ({
  mode: 'template',
  templateKey: '',
  content: { title: '', body: '', ctaLabel: '', ctaUrl: '' },
  topic: '',
  topicClass: 'product',
  channels: ['notification'],
  audience: { kind: 'state', states: ['no_pilot'] },
  delivery: 'window',
  variableValues: {},
});

/**
 * What a test proves: this exact content on these channels. Any change after the test
 * (text, template, channels, variables) invalidates it; the audience does not.
 */
export const contentFingerprint = (s: ComposeState): string =>
  JSON.stringify({
    mode: s.mode,
    templateKey: s.mode === 'template' ? s.templateKey : undefined,
    content: s.mode === 'free' ? s.content : undefined,
    channels: [...s.channels].sort(),
    variableValues: s.variableValues,
    topicClass: s.topicClass,
  });

export const requiresTest = (estimate: AudienceEstimate | undefined): boolean =>
  !estimate || estimate.targeted > TEST_REQUIRED_ABOVE;

export const requiresApproval = (audience: Audience, estimate: AudienceEstimate | undefined): boolean =>
  audience.kind === 'all' || (estimate?.targeted ?? 0) > APPROVAL_REQUIRED_ABOVE;

export type SendBlocker = 'no_content' | 'no_channel' | 'no_estimate' | 'no_recipients' | 'needs_test' | null;

export const hasContent = (s: ComposeState): boolean =>
  s.mode === 'template' ? s.templateKey.trim() !== '' : s.content.title.trim() !== '' && s.content.body.trim() !== '';

export interface TestRecord {
  testId: string;
  fingerprint: string;
}

/** Why "Senden" is disabled, or null when it may be clicked. */
export const sendBlocker = (s: ComposeState, estimate: AudienceEstimate | undefined, test: TestRecord | null): SendBlocker => {
  if (!hasContent(s)) return 'no_content';
  if (s.channels.length === 0) return 'no_channel';
  if (!estimate) return 'no_estimate';
  if (estimate.targeted === 0) return 'no_recipients';
  if (requiresTest(estimate) && (!test || test.fingerprint !== contentFingerprint(s))) return 'needs_test';
  return null;
};

/** The test id to send along — only when it still matches the content. */
export const validTestId = (s: ComposeState, test: TestRecord | null): string | undefined =>
  test && test.fingerprint === contentFingerprint(s) ? test.testId : undefined;

export const buildComposeRequest = (s: ComposeState, test: TestRecord | null, dryRun = false): ComposeRequest => {
  const content: InlineContent | undefined =
    s.mode === 'free'
      ? {
          title: s.content.title.trim(),
          body: s.content.body.trim(),
          ctaLabel: s.content.ctaLabel?.trim() || undefined,
          ctaUrl: s.content.ctaUrl?.trim() || undefined,
          subject: s.content.subject?.trim() || undefined,
        }
      : undefined;
  return {
    topic: s.topic.trim() || (s.mode === 'template' ? s.templateKey : content?.title ?? ''),
    topicClass: s.topicClass,
    channels: s.channels,
    audience: s.audience,
    templateKey: s.mode === 'template' ? s.templateKey : undefined,
    content,
    variableValues: Object.keys(s.variableValues).length ? s.variableValues : undefined,
    delivery: s.delivery,
    testId: validTestId(s, test),
    dryRun: dryRun || undefined,
  };
};
